import { snackbar } from "components/awesome_snackbar/snackbar";
import axiosInstance from "config/axios-config";
import { setToken, removeToken } from "config/storageController";
import { profile } from "context/profile";
import { Role_Route } from "utils/role_route";
import { getCountryFromIP } from "helper/getCountryFromIP";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[1-9]\d{7,14}$/;

const buildLoginPayload = (identifier, password) => {
  const isEmail = EMAIL_REGEX.test(identifier);
  const isPhone = !isEmail && PHONE_REGEX.test(identifier);

  if (!isPhone && !isEmail) {
    return {
      success: false,
      message: "Invalid email or phone format.",
      payload: null,
    };
  }

  const finalIdentifier = isPhone && !identifier.startsWith("+") ? `+${identifier}` : identifier;

  const payload = isPhone
    ? { phone: finalIdentifier, password }
    : { email: finalIdentifier, password };

  return {
    success: true,
    message: "",
    payload,
  };
};

/**
 * Establishes the profile session from a successful full-login response.
 * Shared by direct login and branch-selection login completion.
 * @param {Object} res - Axios response whose data contains the full user payload.
 * @returns {Promise<{success: boolean, message?: string, data?: any}>} Session result.
 */
const finalizeLoginSession = async (res) => {
  const roleRoutes = Role_Route(res.data.data?.role);

  // Check if user has access routes assigned
  if (roleRoutes.length === 0) {
    snackbar("error", "You are not allowed to access the system");
    return { success: false, message: "Access denied: no routes assigned." };
  }

  // Fetch country from IP address
  const countryResult = await getCountryFromIP();
  const country = countryResult.success ? countryResult.country : "Ethiopia";
  const country_code = countryResult.success ? countryResult.country_code : "ET";
  res.data.data.country = country;
  res.data.data.country_code = country_code?.toUpperCase();

  // Map server response data to global profile object
  profileDataMapper(res.data.data);

  setToken(res.data.data.accessToken);

  // Notify the app that a new session started.
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("auth:login"));
  }

  return { success: true, data: res.data.data };
};

/**
 * Logs in a user using email or phone number.
 * When the account is assigned to multiple branches the response carries a
 * short-lived selection token and the user must pick a branch to continue.
 * @param {string} identifier - Email or phone number.
 * @param {string} password - User's password.
 * @returns {Promise<{success: boolean, message?: string}>} Login result.
 */
export const userLogin = async (identifier, password) => {
  let response = {
    success: false,
    message: "",
    requireBranchSelection: false,
    selection_token: null,
    data: null,
  };
  try {
    const { success, message, payload } = buildLoginPayload(identifier, password);

    if (!success) {
      return { success: false, message };
    }

    // Send login request
    const res = await axiosInstance.post("/account/login", payload);
    response.message = res?.data?.message || "Incorrect email/phone or password.";

    if (res?.data?.success) {
      const requiresBranchSelection =
        res?.data?.requireBranchSelection || res?.data?.requireSchoolSelection;

      if (requiresBranchSelection) {
        const selectionToken = res?.data?.selection_token || null;

        if (selectionToken) {
          // Store the selection token so branch-selection requests carry it as the Bearer token
          setToken(selectionToken);
        }

        response = {
          ...response,
          success: true,
          requireBranchSelection: true,
          selection_token: selectionToken,
          data: res?.data?.data || null,
        };
      } else {
        const session = await finalizeLoginSession(res);
        response = { ...response, ...session };
      }
    }
  } catch (error) {
    if (error.response) {
      // Server responded with an error
      response.message = error.response.data?.message || "Server returned an error.";
    } else if (error.request) {
      // Request was made but no response received
      response.message = "Network error: could not reach server.";
    } else {
      // Other JS errors
      response.message = error.message || "An unexpected error occurred.";
    }
  }
  return response;
};

/**
 * Completes the initial branch selection using the short-lived selection token
 * returned by the login endpoint.
 * @param {string} selectionToken - Selection token from the initial login.
 * @param {string} schoolBranchId - Identifier of the branch the user picked.
 * @returns {Promise<{success: boolean, message: string, requireBranchSelection: boolean, selection_token: string|null, data: any}>} Selection result.
 */
export const selectSchoolBranch = async (selectionToken, schoolBranchId) => {
  let response = {
    success: false,
    message: "",
    requireBranchSelection: false,
    selection_token: null,
    data: null,
  };

  if (!selectionToken || !schoolBranchId) {
    return { ...response, message: "A branch must be selected to continue." };
  }

  try {
    const res = await axiosInstance.post("/account/login", {
      selection_token: selectionToken,
      school_branch_id: schoolBranchId,
    });
    response.message = res?.data?.message || "Unable to continue with the selected branch.";

    if (res?.data?.success) {
      const requiresBranchSelection =
        res?.data?.requireBranchSelection || res?.data?.requireSchoolSelection;

      if (requiresBranchSelection) {
        response = {
          ...response,
          success: true,
          requireBranchSelection: true,
          selection_token: res?.data?.selection_token || null,
          data: res?.data?.data || null,
        };
      } else {
        const session = await finalizeLoginSession(res);
        response = { ...response, ...session };
      }
    }
  } catch (error) {
    if (error.response) {
      response.message = error.response.data?.message || "Server returned an error.";
    } else if (error.request) {
      response.message = "Network error: could not reach server.";
    } else {
      response.message = error.message || "An unexpected error occurred.";
    }
  }
  return response;
};

/**
 * Switches the active branch for a logged-in user via /branch/switch. The
 * server validates the branch, blacklists the old tokens and returns a new
 * access token scoped to the new active branch.
 * @param {string} branchId - Identifier of the branch to switch to.
 * @returns {Promise<{success: boolean, message: string, data: any}>} Switch result.
 */
export const switchSchoolBranch = async (branchId) => {
  let response = {
    success: false,
    message: "",
    data: null,
  };

  if (!branchId) {
    return { ...response, message: "A school branch must be selected." };
  }

  try {
    const res = await axiosInstance.post("/branch/switch", { branch_id: branchId });
    response.message = res?.data?.message || "Unable to switch school branch.";

    if (res?.data?.success) {
      const {
        active_branch_id: activeBranchId,
        active_branch: activeBranch,
        accessToken,
      } = res.data.data || {};

      // Apply the newly active branch and token to the global profile
      if (activeBranchId) profile.school_id = activeBranchId;
      if (activeBranch?.name) profile.school_name = activeBranch.name;
      if (activeBranch) profile.current_school = activeBranch;
      if (accessToken) {
        profile.accessToken = accessToken;
        setToken(accessToken);
      }

      response = {
        ...response,
        success: true,
        data: res?.data?.data || null,
      };
    }
  } catch (error) {
    if (error.response) {
      response.message = error.response.data?.message || "Server returned an error.";
    } else if (error.request) {
      response.message = "Network error: could not reach server.";
    } else {
      response.message = error.message || "An unexpected error occurred.";
    }
  }
  return response;
};

/**
 * Ends the session on the server so the HttpOnly refresh cookie is cleared or
 * revoked, then clears the local access token. Local cleanup always runs, even
 * when the request fails, so the user is never left half signed in.
 * @returns {Promise<{success: boolean, message: string}>} Logout result.
 */
export const logoutUser = async () => {
  let response = {
    success: false,
    message: "",
  };
  try {
    const res = await axiosInstance.post("/account/logout");
    response.success = Boolean(res?.data?.success);
    response.message =
      res?.data?.message || (response.success ? "Logged out successfully" : "Unable to log out.");
  } catch (error) {
    console.error("Error logging out:", error);
    response.message =
      error.response?.data?.message ||
      error.message ||
      "An error occurred while logging out. Please check your connection and try again.";
  } finally {
    removeToken();
  }
  return response;
};

/**
 * Maps server user data to the global profile object.
 * Only overwrites fields that are explicitly provided in the response so that
 * partial payloads (e.g. from the /verify endpoint) do not wipe out values
 * (such as country, country_code, accessToken) that were populated at login.
 * @param {Object} data - Server response data for the user.
 */
export const profileDataMapper = (data) => {
  if (!data || typeof data !== "object") return;

  // Basic user identifiers
  profile.staff_id = data.staff_id;
  profile.first_name = data.first_name;
  profile.last_name = data.last_name || "";
  profile.email = data.email || "";
  profile.phone_number = data.phone_number || "";

  // Role & access
  profile.role = data.role || "";
  // Only replaced when the payload carries it, so partial payloads (such as
  // /verify) do not wipe the active session.
  if (data.accessToken) profile.accessToken = data.accessToken;

  // Educational info
  profile.educational_level = data.educational_level || "";
  profile.class_id = data.class_id || null;

  // School info
  const schoolId = data.school_id || data.school?.school_id;
  if (schoolId) profile.school_id = schoolId;
  const schoolName = data.school_name || data.school?.name || "";
  if (schoolName) profile.school_name = schoolName;
  if ("school" in data) profile.current_school = data.school ?? null;
  if (Array.isArray(data.schools_assigned)) profile.schools_assigned = data.schools_assigned;

  // Country info
  if (data.country) profile.country = data.country;
  if (data.country_code) profile.country_code = data.country_code.toUpperCase();

  // Subjects array (make a copy to avoid mutation)
  if (Array.isArray(data.subject_id)) {
    profile.subject_id = [...data.subject_id];
  } else {
    profile.subject_id = [];
  }
};
